import {
	Component,
	Input,
	Output,
	EventEmitter,
	OnChanges,
	OnInit,
	OnDestroy,
	ViewChild,
	ElementRef,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AddressPipe } from 'src/app/components/pipes';
import { Address, emptyAddress } from 'src/app/models/data/address';
import {
	Device,
	DeviceGroupStatus,
	DeviceStatus,
	isSentriVaultDevice,
	PDQ_KMS_GENERATION_1,
	PDQ_SDS_GENERATION_1,
} from 'src/app/models/data/device';
import { SystemCapabilityEnum } from 'src/app/models/data/systemCapability';
import { EditDeviceRequestBody, UserProfile } from 'src/app/models/model';
import {
	ApiLoadingKeys,
	ApiService,
	DeviceRemovalActions,
} from 'src/app/services/api.services';
import { BannerMessageService } from 'src/app/services/banner-message.service';
import { SystemCapabilityService } from 'src/app/services/system-capabilities/system-capability.service';
import { PhotoGalleryComponent } from '../../../../components/photo-gallery/photo-gallery.component';
import { TransferDeviceOwnershipDialogComponent } from '../../flows/transfer-device-ownership-dialog/transfer-device-ownership-dialog.component';
import { DeviceUpdatedEvent } from '../device-details.component';
import DeviceInstallationPhoto = Components.Schemas.DeviceInstallationPhoto;
import { MarkerOnMap } from 'src/app/components/map/map.component';
import {
	LoadingConfirmationDialogComponent,
	LoadingConfirmationDialogComponentData,
} from 'src/app/components/loading-confirmation-dialog/loading-confirmation-dialog.component';
import {
	RemoveDeviceDialogComponent,
	RemoveDeviceDialogData,
} from 'src/app/components/remove-device-dialog/remove-device-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
	AbstractControl,
	FormControl,
	ValidatorFn,
	FormsModule,
	ReactiveFormsModule,
} from '@angular/forms';
import {
	DEVICE_SETTING_AJAX_HUB_ID,
	PDQ_DEVICE_SETTING_AUTO_RELOCK_DELAY,
	PDQ_DEVICE_SETTING_HANDEDNESS,
} from 'src/app/app.constants';
import { MatDivider } from '@angular/material/list';
import { DeviceStatusComponent } from '../../device-status/device-status.component';
import { MatFormField, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MapComponent } from '../../../../components/map/map.component';
import { MatIcon } from '@angular/material/icon';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { NgClass } from '@angular/common';
import {
	AddressPipe as AddressPipe_1,
	UtcToLocal,
} from '../../../../components/pipes';
import {
	InstallationPhotoCompressionService,
	InstallationPhotoError,
} from 'src/app/services/installation-photo-compression.service';

@Component({
	selector: 'app-device-about',
	templateUrl: './device-about.component.html',
	styleUrls: ['./device-about.component.scss', '../../../common/main.scss'],
	providers: [AddressPipe],
	imports: [
		MatDivider,
		DeviceStatusComponent,
		MatFormField,
		MatInput,
		FormsModule,
		MapComponent,
		MatIcon,
		MatSlideToggle,
		ReactiveFormsModule,
		MatError,
		NgClass,
		AddressPipe_1,
		UtcToLocal,
	],
})
export class DeviceAboutComponent implements OnInit, OnChanges, OnDestroy {
	@Input() device!: Device;
	@Input() deviceInstallationPhotos: DeviceInstallationPhoto[] = [];
	// Get the feature flags from the parent component to avoid displaying maps while waiting for the API call
	@Input() isHaspCodeGenFeatureEnabled: boolean = false;
	@Input() isInstallPhotoManagementFeatureEnabled: boolean = false;
	@Input() isTransferDeviceUpdatesFfEnabled: boolean = false;
	@Output() deviceUpdatedEvents = new EventEmitter<DeviceUpdatedEvent>();

	@ViewChild('uninstallLockBtn')
	uninstallLockBtn!: ElementRef<HTMLButtonElement>;

	canEditDevices: boolean;
	isOracle: boolean;
	editDevice: EditDevice = { keyDetection: true };
	hasTransferDeviceInventoryOwnershipCapability: boolean;
	hasIssueFlexCodesSystemCapability: boolean;
	activeCompanyId: string | undefined = '';
	isEditing = false;
	isEditingSettings = false;
	deviceMarker: MarkerOnMap[] = [{}] as MarkerOnMap[];
	mapCenter: google.maps.LatLngLiteral = {} as google.maps.LatLngLiteral;
	allowHaspCodeGen: boolean = false;
	deviceIsExpired: boolean = true;
	isPDQ: boolean = false;
	delayContinueButton = true;
	deviceSettingsKV: {
		handedness?: string;
		autoRelockDelay?: string;
		ajaxHubId?: string;
	} = {};
	finishedInitDeviceSettingsKVLoading: boolean = false;
	companyHasDeviceRemovalAccessOptionsEnabled: boolean = false;
	displayUninstallFlagForSentriVault: boolean = false;
	editInstallationPhoto: DeviceInstallationPhoto | undefined;
	editInstallationPhotoCaption = '';
	isInstallationPhotoDeletePending = false;
	selectedInstallationPhotoFile: File | undefined;
	selectedInstallationPhotoPreviewUrl = '';
	installationPhotoError = '';
	isPreparingInstallationPhoto = false;
	isSavingDeviceDetails = false;
	locationNameTouched: boolean = false;

	static SENTRILOCK_HQ_LAT: any = 39.363522;
	static SENTRILOCK_HQ_LNG: any = -84.366302;

	readonly deviceLocationName = $localize`:@@device-detailLocationName:Location Name`;
	readonly deviceDetailsEditWarningHeader = $localize`:@@deviceAbout-editWarningHeader:Any changes here are permanent.`;
	readonly deviceDetailsEditWarningBody = $localize`:@@deviceAbout-editWarningBody:Review your location name and photo before saving — deleted photos can't be recovered.`;

	limitedAlphanumericValidator: ValidatorFn = (control: AbstractControl) => {
		const value = control.value;
		if (value === null || value === '') return null;

		const isValid = /^[A-Fa-f0-9]+$/.test(value);
		return isValid ? null : { limitedAlphanumeric: true };
	};

	ajaxHubIdFormControl = new FormControl('', [
		this.limitedAlphanumericValidator,
	]);

	get ownerName(): string {
		return this.device?.activeDeviceAssignment?.company?.name ?? 'No Owner';
	}

	get deviceAddress(): Address {
		return (
			this.device?.activeDeviceAssignment?.activeInstallation
				?.installationLocation?.location?.address ?? emptyAddress()
		);
	}

	constructor(
		private readonly scService: SystemCapabilityService,
		private readonly dialog: MatDialog,
		private readonly router: Router,
		private readonly apiService: ApiService,
		private bannerMessageService: BannerMessageService,
		private readonly snackbar: MatSnackBar,
		private readonly installationPhotoCompressionService: InstallationPhotoCompressionService
	) {
		this.canEditDevices = this.scService.has(
			SystemCapabilityEnum.deviceManagement
		);
		this.isOracle = this.scService.isOracle;
		this.hasTransferDeviceInventoryOwnershipCapability = this.scService.has(
			SystemCapabilityEnum.transferDeviceInventoryOwnership
		);
		this.hasIssueFlexCodesSystemCapability = this.scService.has(
			SystemCapabilityEnum.issueFlexCodes
		);
	}

	async ngOnInit(): Promise<void> {
		await this.setActiveCompanyId();
		await this.loadDeviceProperties();
	}

	async ngOnChanges(): Promise<void> {
		await this.loadDeviceProperties();
		this.setupEditDevice();
	}

	ngOnDestroy(): void {
		this.clearSelectedInstallationPhoto();
	}

	isLocationNameEditable(): boolean {
		return (
			this.isEditing &&
			this.hasValidInstallationLocation() &&
			(this.activeCompanyId === this.editDevice.companyId ||
				(this.canEditInheritedDevice() &&
					!!this.device?.activeDeviceAssignment?.activeInstallation
						?.installationLocation))
		);
	}

	isLocationNameInvalid(): boolean {
		return (
			this.isLocationNameEditable() &&
			(this.editDevice.locationName ?? '').trim() === ''
		);
	}

	shouldShowLocationNameValidation(): boolean {
		return this.locationNameTouched && this.isLocationNameInvalid();
	}

	onLocationNameBlur(): void {
		this.locationNameTouched = true;
	}

	isSaveDeviceDetailsDisabled(): boolean {
		return (
			this.isLocationNameInvalid() ||
			this.isPreparingInstallationPhoto ||
			this.isSavingDeviceDetails ||
			!!this.installationPhotoError
		);
	}

	isDeviceExpired(device: Device): boolean {
		const { displayStatus, groupStatus } = device;
		const expiredList = ['expired'];

		// Check if either displayStatus or groupStatus is in the expiredList
		return (
			expiredList.includes(displayStatus ?? '') ||
			expiredList.includes(groupStatus ?? '')
		);
	}

	async canUserGenerateHaspCode(): Promise<boolean> {
		if (!this.device) {
			this.allowHaspCodeGen = false;
			return this.allowHaspCodeGen;
		}

		if (
			this.activeCompanyId === this.device?.activeDeviceAssignment?.companyId
		) {
			// users company owns the device so they are allowed to generate a hasp code for it
			this.allowHaspCodeGen = true;
		} else {
			// the users company doesn't own the device so we need to check if they have been given that ability through inheritence
			if (!this.hasIssueFlexCodesSystemCapability) {
				// if they don't have hasp code gen capabilities, don't check this using the route (otherwise it logs the user out)
				this.allowHaspCodeGen = false;
			} else {
				const deviceControlSummary =
					await this.apiService.getInheritedDeviceGroupControlByDeviceId(
						this.device.id
					);

				if (deviceControlSummary) {
					deviceControlSummary.forEach((item) => {
						item.assignments.forEach((val) => {
							const currentDate = new Date();
							const startDate = new Date(val.utcStartDate);
							const endDate = val.utcEndDate ? new Date(val.utcEndDate) : null;

							if (!(
								currentDate >= startDate &&
								(!endDate || currentDate <= endDate)
							)) {
								return;
							}

							if (val.allowHaspCodeGeneration) {
								this.allowHaspCodeGen = true;
							}
						});
					});
				}
			}
		}

		return this.allowHaspCodeGen;
	}

	/**
	 * Checks if user can edit devices from inheriting companies.
	 * Returns true if user has Device Management capability and the device is from an inherited company.
	 */
	canEditInheritedDevice(): boolean {
		// Check if this is an inherited device (user's company doesn't own it)
		const isInheritedDevice =
			this.activeCompanyId !== this.editDevice.companyId;

		// User can edit inherited devices if they have Device Management capability
		return isInheritedDevice && this.canEditDevices;
	}

	/**
	 * Checks if the device has a valid installation location that can be edited.
	 * Returns true only if the device has an installation location with a valid Location reference.
	 */
	hasValidInstallationLocation(): boolean {
		const installationLocation =
			this.device?.activeDeviceAssignment?.activeInstallation
				?.installationLocation;

		// Must have installation location AND it must reference a valid Location (property)
		return !!installationLocation && !!installationLocation.location;
	}

	setupEditDevice(): void {
		if (!this.device) {
			return;
		}

		this.editDevice = {
			serialNumber: this.device?.serialNumber,
			typeOfDevice: this.device.deviceType,
			cartonNumber: this.device.cartonNumber,
			status: this.device?.status,
			palletNumber: this.device.palletNumber,
			dateArrived: this.device.utcDateArrived,
			locationName:
				this.device?.activeDeviceAssignment?.activeInstallation
					?.installationLocation?.name ?? '',
			// locationPhotoUrl: this.device.locationPhotoUrl,
			companyId: this.device?.activeDeviceAssignment?.companyId,
			readyForRemoval:
				!this.device?.activeDeviceAssignment?.activeInstallation
					?.utcUninstallDate &&
				!!this.device?.activeDeviceAssignment?.activeInstallation
					?.utcRemovalRequested,

			propertyName:
				this.device?.activeDeviceAssignment?.activeInstallation
					?.installationLocation?.location?.name,
			latitude: this.deviceAddress?.geoCoordinates?.latitude,
			longitude: this.deviceAddress?.geoCoordinates?.longitude,
			address1: this.deviceAddress?.address1,
			address2: this.deviceAddress?.address2,
			locality: this.deviceAddress?.locality,
			stateProvinceRegion: this.deviceAddress?.stateProvinceRegion,
			postalCode: this.deviceAddress?.postalCode,
			keyDetection: this.device.keyDetection ?? true,
		};
		this.setupEditInstallationPhoto();

		this.setMapCenter();
		this.setMapPin();
	}

	setupEditInstallationPhoto(): void {
		this.clearSelectedInstallationPhoto();
		this.editInstallationPhoto = this.deviceInstallationPhotos[0];
		this.editInstallationPhotoCaption =
			this.editInstallationPhoto?.caption ?? '';
		this.isInstallationPhotoDeletePending = false;
	}

	async setActiveCompanyId(): Promise<void> {
		const userProfile: UserProfile = await this.apiService.getUserProfile();
		this.activeCompanyId = userProfile?.activeRoleDetails?.company?.id;
	}

	/**
	 * Loads and recalculates all device-specific properties and settings.
	 * Should be called whenever the device changes or is initially loaded.
	 */
	async loadDeviceProperties(): Promise<void> {
		if (!this.device) {
			return;
		}

		// Recalculate device state properties
		this.deviceIsExpired =
			this.isDeviceExpired(this.device) &&
			this.isTransferDeviceUpdatesFfEnabled;
		this.isPDQ =
			(this.device?.deviceGeneration
				?.toLowerCase()
				?.includes(PDQ_KMS_GENERATION_1) ||
				this.device?.deviceGeneration
					?.toLowerCase()
					?.includes(PDQ_SDS_GENERATION_1)) ??
			false;

		// Only display uninstall option for SentriVault devices that are in service :
		this.displayUninstallFlagForSentriVault =
			this.device.status === 'inService' && isSentriVaultDevice(this.device);

		// Recalculate hasp code generation capability
		await this.canUserGenerateHaspCode();

		// Fetch device settings KV for the device
		this.finishedInitDeviceSettingsKVLoading = false;
		const deviceSettingKVRecords =
			await this.apiService.getDeviceDeviceSettingsKV(this.device.id);
		this.deviceSettingsKV = {}; // Reset settings
		deviceSettingKVRecords.forEach((setting) => {
			if (setting.settingName === PDQ_DEVICE_SETTING_HANDEDNESS) {
				this.deviceSettingsKV.handedness = setting.value;
			} else if (setting.settingName === PDQ_DEVICE_SETTING_AUTO_RELOCK_DELAY) {
				this.deviceSettingsKV.autoRelockDelay = setting.value;
			} else if (setting.settingName === DEVICE_SETTING_AJAX_HUB_ID) {
				this.deviceSettingsKV.ajaxHubId = setting.value;
				this.ajaxHubIdFormControl.setValue(this.deviceSettingsKV.ajaxHubId);
			}
		});
		this.finishedInitDeviceSettingsKVLoading = true;

		// Recalculate company device removal setting
		try {
			const companySetting = await this.apiService.getCompanySettingByName(
				'deviceRemovalAccessOptions'
			);
			this.companyHasDeviceRemovalAccessOptionsEnabled =
				companySetting?.value?.toLowerCase() === 'true' ||
				companySetting?.value === '1';
		} catch (err) {
			this.companyHasDeviceRemovalAccessOptionsEnabled = false;
		}
	}

	isValid(): boolean {
		// TODO: isValid not yet implemented
		return true;
	}

	saveEdits(): void {
		alert('Save Device not yet implemented.');

		// TODO: Save User not yet implemented in API
		// this.apiService.editPropertyById(this.propertyId, requestBody)
		//   .then(async _ => {
		//     await this.requestProperty();
		//     this.bannerMessageService.sendMessage('Successfully saved property.');
		//   });
	}

	transferOwnershipClicked(): void {
		this.dialog
			.open(TransferDeviceOwnershipDialogComponent, {
				panelClass: 'border-radius-dialog',
				data: {
					devices: [this.device],
					originalCompany: this.device?.activeDeviceAssignment?.company,
				},
			})
			.afterClosed()
			.subscribe((dialogResult: any) => {
				if (dialogResult) {
					this.router.navigate(['/devices']);
				}
			});
	}

	showPhotoGallery(): void {
		if (this.isInstallationPhotoDeletePending) {
			return;
		}

		const photoGalleryItems = this.deviceInstallationPhotos.map((it) => ({
			photoUrl: it.photoUrl,
			caption: it.caption,
		}));
		this.dialog.open(PhotoGalleryComponent, { data: photoGalleryItems });
	}

	setMapCenter() {
		if (this.editDevice.latitude && this.editDevice.longitude) {
			this.mapCenter = {
				lat: this.editDevice.latitude,
				lng: this.editDevice.longitude,
			};
		} else {
			// Set map center to Sentrilock HQ
			this.mapCenter = {
				lat: DeviceAboutComponent.SENTRILOCK_HQ_LAT,
				lng: DeviceAboutComponent.SENTRILOCK_HQ_LNG,
			};
		}
	}

	setMapPin() {
		if (this.editDevice.latitude && this.editDevice.longitude) {
			this.deviceMarker = [
				{
					position: {
						lat: this.editDevice.latitude as any,
						lng: this.editDevice.longitude as any,
					},
				},
			];
		}
	}

	// Un-install Vault / Senturians from property using below model popup for confirmation :
	onUninstallLockClicked(): void {
		const data: LoadingConfirmationDialogComponentData = {
			apiFunctionToExecute: () =>
				this.apiService.requestDeviceRemovalAction(
					this.device.id,
					DeviceRemovalActions.perform
				),
			loadingKeyOfApiFunction: ApiLoadingKeys.requestDeviceRemovalAction,
			dialogDivider: true,
			disableContinueUponOpen: true,
			headerTextBeforeLoading: $localize`:@@deviceAboutTab-confirm:Uninstall Lock`,
			bodyTextBeforeLoading: $localize`:@@deviceAboutTab-deletionConfirmationText:Are you sure you want to uninstall this lock from the system? Once uninstalled, the lock will lose all access functionality until it is re-installed and reconnected with the mobile app.`,
			negativeButtonDialogText: $localize`:@@deviceAboutTab-cancel:Cancel`,
			positiveButtonDialogText: $localize`:@@deviceAboutTab-delete:Uninstall Lock`,

			// Working but removed the code for now because its in discussion with stack-holders for Sentri-Vault naming convention : //
			// headerTextBeforeLoading: this.displayUninstallFlagForSentriVault
			// 	? $localize`:@@deviceAbout-uninstallVaultDialogTitle:Uninstall Vault`
			// 	: $localize`:@@deviceAboutTab-confirm:Uninstall Lock`,
			// bodyTextBeforeLoading: this.displayUninstallFlagForSentriVault
			// 	? $localize`:@@deviceAbout-uninstallVaultDialogDescription:Are you sure you want to uninstall this vault from the system? Once uninstalled, the vault will lose all access functionality until it is re-installed and reconnected with the mobile app.`
			// 	: $localize`:@@deviceAboutTab-deletionConfirmationText:Are you sure you want to uninstall this lock from the system? Once uninstalled, the lock will lose all access functionality until it is re-installed and reconnected with the mobile app.`,
			// negativeButtonDialogText: $localize`:@@deviceAboutTab-cancel:Cancel`,
			// positiveButtonDialogText: this.displayUninstallFlagForSentriVault
			// 	? $localize`:@@deviceAbout-uninstallVaultDialogConfirmButtonTitle:Uninstall Vault`
			// 	: $localize`:@@deviceAboutTab-delete:Uninstall Lock`,

			negativeButtonClass: 'secondary-small-button',
			positiveButtonClass: 'warning-small-button-wider',
		};
		const dialogRef: MatDialogRef<LoadingConfirmationDialogComponent> =
			this.dialog.open(LoadingConfirmationDialogComponent, {
				maxWidth: '510px',
				panelClass: 'pop-up-dialog-v3',
				data: data,
			});

		dialogRef.afterClosed().subscribe(async (result: any) => {
			if (!!result) {
				this.isEditing = false;
				this.deviceUpdatedEvents.emit({
					deviceId: this.device.id,
					updated: true,
				});

				const deviceLabel = this.displayUninstallFlagForSentriVault
					? $localize`:@@deviceAboutTab-confirmRemovalVaultMsg1:SentriVault -`
					: $localize`:@@deviceAboutTab-confirmRemovalMsg1:Senturian -`;

				this.snackbar.open(
					`${deviceLabel} ${this.device.serialNumber} ${$localize`:@@deviceAboutTab-confirmRemovalMsg2:has been uninstalled and is available.`}`,
					undefined,
					{
						duration: 2000,
					}
				);
			}
			document.getElementById('uninstallLockBtn')?.blur();
		});
	}

	onEditClicked(): void {
		this.isEditing = true;
		this.locationNameTouched = false;
	}

	onCancelEditClicked(): void {
		this.isEditing = false;
		this.locationNameTouched = false;
		this.editDevice.locationName =
			this.device?.activeDeviceAssignment?.activeInstallation
				?.installationLocation?.name ?? '';
		this.setupEditInstallationPhoto();
	}

	async onSaveDeviceDetailChangesClicked(): Promise<void> {
		if (this.isLocationNameInvalid()) {
			this.locationNameTouched = true;
			return;
		}
		if (this.isSaveDeviceDetailsDisabled()) {
			return;
		}

		const trimmedLocationName = (this.editDevice.locationName ?? '').trim();
		const currentLocationName = (
			this.device?.activeDeviceAssignment?.activeInstallation
				?.installationLocation?.name ?? ''
		).trim();

		// Check if user can edit: either they own the device or have inherited edit rights
		const isDeviceOwner = this.activeCompanyId === this.editDevice.companyId;
		const canEditDevice =
			(isDeviceOwner || this.canEditInheritedDevice()) && this.canEditDevices;

		// Verify that the device has a valid installation location before allowing edits
		if (!this.hasValidInstallationLocation()) {
			this.bannerMessageService.sendMessage(
				$localize`:@@deviceAbout-invalidInstallationLocation:Cannot update location name: device does not have a valid installation location.`
			);
			return;
		}

		const editDeviceRequestBody: EditDeviceRequestBody = {};

		// if the user is allowed to edit the installation location name and it's been changed then save the changes
		if (
			canEditDevice &&
			trimmedLocationName !== '' &&
			trimmedLocationName !== currentLocationName
		) {
			editDeviceRequestBody.installationLocationName = trimmedLocationName;
		}

		if (
			this.canManageInstallationPhoto() &&
			this.editInstallationPhoto &&
			!this.selectedInstallationPhotoFile
		) {
			const captionChanged =
				this.editInstallationPhotoCaption !==
				(this.editInstallationPhoto.caption ?? '');

			if (this.isInstallationPhotoDeletePending) {
				editDeviceRequestBody.installationPhotoId =
					this.editInstallationPhoto.id;
				editDeviceRequestBody.deleteInstallationPhoto = true;
			} else if (captionChanged) {
				editDeviceRequestBody.installationPhotoId =
					this.editInstallationPhoto.id;
				editDeviceRequestBody.installationPhotoCaption =
					this.editInstallationPhotoCaption;
			}
		}

		if (
			Object.keys(editDeviceRequestBody).length === 0 &&
			!this.selectedInstallationPhotoFile
		) {
			this.isEditing = false;
			this.bannerMessageService.sendMessage(
				$localize`:@@deviceAbout-noChangesToSave:There aren't any changes to save`
			);
			return;
		}

		let metadataSaved = false;
		this.isSavingDeviceDetails = true;
		try {
			let updateSuccessful = true;
			if (Object.keys(editDeviceRequestBody).length > 0) {
				updateSuccessful = await this.apiService.editDeviceById(
					this.device.id,
					editDeviceRequestBody
				);
				metadataSaved = updateSuccessful;
			}

			if (updateSuccessful && this.selectedInstallationPhotoFile) {
				await this.apiService.saveDeviceInstallationPhoto(
					this.device.id,
					this.selectedInstallationPhotoFile,
					this.editInstallationPhotoCaption
				);
			}

			if (updateSuccessful) {
				this.isEditing = false;
				this.clearSelectedInstallationPhoto();
				this.bannerMessageService.sendMessage(
					$localize`:@@deviceAbout-deviceUpdatesSaved:Device updates saved.`
				);
				this.deviceUpdatedEvents.emit({
					deviceId: this.device.id,
					updated: true,
				});
			} else {
				this.bannerMessageService.sendMessage(
					$localize`:@@deviceAbout-unableToSaveDeviceUpdates:Unable to save device updates. Please try again.`
				);
			}
		} catch (err) {
			if (metadataSaved) {
				this.deviceUpdatedEvents.emit({
					deviceId: this.device.id,
					updated: true,
				});
			}
			this.bannerMessageService.sendMessage(
				metadataSaved && !!this.selectedInstallationPhotoFile
					? $localize`:@@deviceAbout-photoUploadPartialFailure:Device details were saved, but the installation photo could not be uploaded. Please try again.`
					: $localize`:@@deviceAbout-errorSavingDeviceUpdates:An error occurred while saving device updates. Please try again.`
			);
		} finally {
			this.isSavingDeviceDetails = false;
		}
	}

	canManageInstallationPhoto(): boolean {
		return (
			this.isInstallPhotoManagementFeatureEnabled &&
			this.canEditDevices &&
			this.activeCompanyId === this.editDevice.companyId &&
			this.hasValidInstallationLocation() &&
			!!this.device?.activeDeviceAssignment?.activeInstallation &&
			!this.isSenturianDevice()
		);
	}

	canEditInstallationPhotoCaption(): boolean {
		return (
			this.canManageInstallationPhoto() &&
			(!!this.editInstallationPhoto || !!this.selectedInstallationPhotoFile) &&
			!this.isInstallationPhotoDeletePending
		);
	}

	shouldShowInstallationPhotoCaption(): boolean {
		return (
			!this.isInstallationPhotoDeletePending &&
			(!!this.editInstallationPhoto ||
				!!this.selectedInstallationPhotoFile ||
				this.deviceInstallationPhotos.length > 0)
		);
	}

	shouldShowInstallationPhotoSection(): boolean {
		return (
			this.canManageInstallationPhoto() &&
			(this.deviceInstallationPhotos.length > 0 ||
				this.isInstallationPhotoDeletePending ||
				this.isEditing)
		);
	}

	onDeleteInstallationPhotoClicked(): void {
		if (!this.canEditInstallationPhotoCaption()) {
			return;
		}

		if (this.selectedInstallationPhotoFile) {
			this.clearSelectedInstallationPhoto();
			this.editInstallationPhotoCaption =
				this.editInstallationPhoto?.caption ?? '';
			return;
		}

		this.isInstallationPhotoDeletePending = true;
		this.editInstallationPhotoCaption = '';
	}

	async onInstallationPhotoSelected(event: Event): Promise<void> {
		const input = event.target as HTMLInputElement;
		const file = input.files?.[0];
		input.value = '';
		if (!file) {
			return;
		}

		this.installationPhotoError = '';
		this.isPreparingInstallationPhoto = true;
		try {
			const preparedFile =
				await this.installationPhotoCompressionService.prepare(file);
			this.clearSelectedInstallationPhoto();
			this.selectedInstallationPhotoFile = preparedFile;
			this.selectedInstallationPhotoPreviewUrl =
				URL.createObjectURL(preparedFile);
			this.editInstallationPhotoCaption = '';
			this.isInstallationPhotoDeletePending = false;
		} catch (err) {
			this.installationPhotoError = this.getInstallationPhotoError(err);
		} finally {
			this.isPreparingInstallationPhoto = false;
		}
	}

	private getInstallationPhotoError(err: unknown): string {
		if (err instanceof InstallationPhotoError) {
			switch (err.code) {
				case 'unsupportedType':
					return $localize`:@@deviceAbout-installPhotoUnsupportedType:This file type is not supported. Please upload a JPEG or PNG image.`;
				case 'originalTooLarge':
					return $localize`:@@deviceAbout-installPhotoOriginalTooLarge:File size cannot exceed 15MB.`;
				case 'compressedTooLarge':
					return $localize`:@@deviceAbout-installPhotoCompressedTooLarge:This image could not be compressed below 2 MB. Please choose a smaller image.`;
			}
		}
		return $localize`:@@deviceAbout-installPhotoUnableToRead:This image could not be read. Please choose another JPEG or PNG image.`;
	}

	private clearSelectedInstallationPhoto(): void {
		if (this.selectedInstallationPhotoPreviewUrl) {
			URL.revokeObjectURL(this.selectedInstallationPhotoPreviewUrl);
		}
		this.selectedInstallationPhotoFile = undefined;
		this.selectedInstallationPhotoPreviewUrl = '';
		this.installationPhotoError = '';
	}

	shouldShowImageUploadPlaceholder(): boolean {
		return (
			this.isEditing &&
			this.canManageInstallationPhoto() &&
			!this.selectedInstallationPhotoFile &&
			(this.isInstallationPhotoDeletePending ||
				this.deviceInstallationPhotos.length === 0)
		);
	}

	private isSenturianDevice(): boolean {
		return (
			this.device?.deviceType?.toLowerCase().includes('senturian') ||
			this.device?.deviceMarketingName?.toLowerCase().includes('senturian') ||
			false
		);
	}

	onEditSettingsClicked(): void {
		this.isEditingSettings = true;
	}

	onCancelEditSettingsClicked(): void {
		this.isEditingSettings = false;
		// if the user wants to cancel any changes they made we need to setup the fields in edit settings again.
		this.setupEditDevice();
	}

	async onSaveSettingsClicked(): Promise<void> {
		try {
			const currentAjaxHubId = (this.ajaxHubIdFormControl.value ?? '').trim();
			const savedAjaxHubId = this.deviceSettingsKV.ajaxHubId ?? '';
			if (
				this.editDevice.keyDetection === this.device.keyDetection &&
				savedAjaxHubId === currentAjaxHubId
			) {
				this.bannerMessageService.sendMessage(
					$localize`:@@deviceAbout-noChangesToSave:There aren't any changes to save`
				);
			} else {
				if (
					this.editDevice.keyDetection !== this.device.keyDetection ||
					savedAjaxHubId !== currentAjaxHubId
				) {
					await this.apiService.updateDeviceSettings(this.device.id, {
						ajaxHubId:
							savedAjaxHubId !== currentAjaxHubId
								? currentAjaxHubId
								: undefined,
						keyDetection:
							this.editDevice.keyDetection !== this.device.keyDetection
								? this.editDevice.keyDetection
								: undefined,
					});
					this.deviceSettingsKV.ajaxHubId = currentAjaxHubId;
				}
				this.bannerMessageService.sendMessage(
					$localize`:@@deviceAbout-deviceSettingsSaved:Device settings saved.`
				);
				this.isEditingSettings = false;
				this.deviceUpdatedEvents.emit({
					deviceId: this.device.id,
					updated: true,
				});
			}
		} catch (err) {
			this.bannerMessageService.sendMessage(
				$localize`:@@deviceAbout-errorSavingDeviceSettings:An error occurred while saving device settings. Please try again.`
			);
		}
	}

	async readyForRemovalClicked(): Promise<void> {
		// Check if company has device removal dialog setting enabled
		if (this.companyHasDeviceRemovalAccessOptionsEnabled) {
			this.openRemoveDeviceDialog();
		} else {
			// Direct removal request
			try {
				const removalRequestResponse =
					await this.apiService.requestDeviceRemovalAction(
						this.device.id,
						DeviceRemovalActions.request
					);
				this.bannerMessageService.sendMessage(
					$localize`:@@deviceAbout-deviceRemovalRequested:Device removal requested.`
				);
				this.editDevice.readyForRemoval = true;
				this.isEditing = false;
				this.deviceUpdatedEvents.emit({
					deviceId: this.device.id,
					updated: true,
				});
				return removalRequestResponse;
			} catch (err) {
				this.bannerMessageService.sendMessage(
					$localize`:@@deviceAbout-errorRequestingDeviceRemoval:An error occurred while requesting device removal. Please try again.`
				);
			}
		}
	}

	openRemoveDeviceDialog(): void {
		const dialogRef = this.dialog.open(RemoveDeviceDialogComponent, {
			width: '510px',
			panelClass: 'mobile-friendly-curved-action-dialog',
			data: {
				deviceId: this.device.id,
				deviceName: this.device.serialNumber,
				device: this.device,
			} as RemoveDeviceDialogData,
		});

		dialogRef.afterClosed().subscribe((result) => {
			if (result && result.success) {
				// Update the device state to reflect removal request
				this.editDevice.readyForRemoval = true;
				this.isEditing = false;
				this.deviceUpdatedEvents.emit({
					deviceId: this.device.id,
					updated: true,
				});
			}
		});
	}

	async clearRemovalStatusClicked(): Promise<void> {
		try {
			const removalRequestResponse =
				await this.apiService.requestDeviceRemovalAction(
					this.device.id,
					DeviceRemovalActions.cancel
				);
			this.bannerMessageService.sendMessage(
				$localize`:@@deviceAbout-canceledDeviceRemovalRequest:Canceled device removal request.`
			);
			this.editDevice.readyForRemoval = false;
			this.isEditing = false;
			this.deviceUpdatedEvents.emit({
				deviceId: this.device.id,
				updated: true,
			});
			return removalRequestResponse;
		} catch (err) {
			this.bannerMessageService.sendMessage(
				$localize`:@@deviceAbout-errorClearingRemovalStatus:An error occurred while clearing the removal status. Please try again.`
			);
		}
	}

	isInvalidString(str?: string): boolean {
		return typeof str !== 'string' || str!.trim() === '';
	}

	getApiService() {
		return this.apiService;
	}
}

type EditDevice = {
	serialNumber?: string;
	typeOfDevice?: string;
	cartonNumber?: string;
	status?: DeviceStatus;
	palletNumber?: string;
	dateArrived?: string;
	locationName?: string;
	locationPhotoUrl?: string;
	readyForRemoval?: boolean;

	propertyName?: string;
	latitude?: number;
	longitude?: number;
	address1?: string;
	address2?: string;
	locality?: string;
	stateProvinceRegion?: string;
	postalCode?: string;
	propertyStatus?: string;

	groupStatus?: DeviceGroupStatus;

	companyId?: string;

	keyDetection: boolean;
};
